import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Mic2, Menu, X, LogIn, UserPlus } from 'lucide-react';
import { isAuthenticated, clearAuth, getCurrentUser } from '../utils/auth';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [_user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Check authentication status on mount and route changes
  React.useEffect(() => {
    const checkAuth = () => {
      const hasAuth = isAuthenticated();
      const currentUser = getCurrentUser();
      
      setIsLoggedIn(hasAuth);
      setUser(currentUser);
    };

    checkAuth();
    // Re-check on route change
  }, [location]);

  const handleLogout = () => {
    clearAuth(); // Use centralized auth utility
    setIsLoggedIn(false);
    setUser(null);
    navigate('/');
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Submit Music', path: '/upload' },
    { name: 'Find Reviewers', path: '/reviewers' },
    { name: 'Café', path: '/cafe' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="bg-black p-2 rounded-lg group-hover:bg-[#F2D857] transition-colors">
              <Mic2 className="h-6 w-6 text-[#F2D857] group-hover:text-black" />
            </div>
            <span className="text-xl font-bold font-montserrat">
              Mic Check <span className="text-[#F2D857]">Millionaire</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors relative group ${
                  isActive(link.path)
                    ? 'text-black'
                    : 'text-gray-600 hover:text-black'
                }`}
              >
                {link.name}
                <span
                  className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-[#F2D857] transition-all group-hover:w-full ${
                    isActive(link.path) ? 'w-full' : ''
                  }`}
                />
              </Link>
            ))}
          </div>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            {isLoggedIn ? (
              <>
                {/* Admin Panel removed - admins access via direct URL only (/admin/login) */}
                <Button
                  variant="ghost"
                  onClick={() => {
                    // All users go to reviewer-dashboard (mock dashboard removed)
                    navigate('/reviewer-dashboard');
                  }}
                  className="text-gray-700 hover:text-black hover:bg-gray-100"
                >
                  Dashboard
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="text-gray-700 hover:text-black hover:bg-gray-100"
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="text-gray-700 hover:text-black hover:bg-gray-100"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  Login
                </Button>
                <Button
                  onClick={() => navigate('/signup')}
                  className="bg-black hover:bg-[#F2D857] hover:text-black text-white transition-all"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Sign Up
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-4 pt-2 pb-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-gray-100 text-black'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-black'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-4 space-y-2">
              {isLoggedIn ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      // All users go to reviewer-dashboard (mock dashboard removed)
                      navigate('/reviewer-dashboard');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full justify-center"
                  >
                    Dashboard
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full justify-center"
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      navigate('/login');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full justify-center"
                  >
                    <LogIn className="h-4 w-4 mr-2" />
                    Login
                  </Button>
                  <Button
                    onClick={() => {
                      navigate('/signup');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full justify-center bg-black hover:bg-[#F2D857] hover:text-black text-white"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Sign Up
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;